import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NON_CALLS } from '@rsos/constants/incidentTypes';
import { useFlag } from '@rsos/flags';
import {
  formatTransportStatuses,
  parseTransportVerbatimMessage,
  useGetTransportsQuery,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import InfoWindowContent from '../../InfoWindow/InfoWindowContent';
import { createWebMercatorViewport } from '../utils';
import { StyledCallPopover, StyledPopoverCaret } from './MapPopover.styles';

const MemoizedNonCallMapPopover = React.memo(({ x, y, data }) => {
  const coord = {
    x: x || 0,
    y: y || 0,
  };

  if (data?.nonCallData?.body) {
    let normalizedY = coord.y;

    return (
      <StyledCallPopover x={coord.x} y={normalizedY}>
        <InfoWindowContent type={NON_CALLS} data={data?.nonCallData} />
        <StyledPopoverCaret isGoogleMap />
      </StyledCallPopover>
    );
  }
  return <></>;
});

const NonCallMapPopover = ({ overlay }) => {
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const nonCallPopover = useSelector(state => state.mapPopover.nonCallPopover);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const selectedCallHasLocation = getMostRecentLoc(callQueue[selectedCallerID]);
  const selectedAirAmbulance = useSelector(
    state => state.airAmbulance.selectedAirAmbulance,
  );
  const nonCallAirAmbulances = useSelector(
    state => state.airAmbulance.requests.nonCalls,
  );
  const hoveredID = useSelector(
    state => state.airAmbulance.hoveredAirAmbulance,
  );
  const nonCallPopoverNormalized = useMemo(() => {
    if (nonCallPopover) {
      return nonCallPopover;
    } else {
      // Only show popover for a non call when in all view or when it's selected
      if (!selectedCallHasLocation) {
        if (selectedAirAmbulance) {
          if (hoveredID === selectedAirAmbulance.airAmbulanceID) {
            return nonCallAirAmbulances[hoveredID];
          }
        } else {
          if (hoveredID) {
            if (nonCallAirAmbulances[hoveredID]) {
              return nonCallAirAmbulances[hoveredID];
            }
          }
        }
      }
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nonCallAirAmbulances,
    hoveredID,
    nonCallPopover,
    selectedCallHasLocation,
  ]);

  // When ETL is on, requests in Redux only carry { location }. Status, timestamp,
  // and trip_reason live in the ETL transports query — pull them in so the
  // popover doesn't fall back to "Pending".
  const isEtlIntegrationEnabled = useFlag('air_ambulance_etl_integrations');
  const { data: transportsData } = useGetTransportsQuery(undefined, {
    skip: !isEtlIntegrationEnabled,
  });

  const enrichedPopoverData = useMemo(() => {
    if (!nonCallPopoverNormalized) return null;
    if (!isEtlIntegrationEnabled) return nonCallPopoverNormalized;

    const transportId = nonCallPopoverNormalized.topic?.split('//')[1];
    const transport = transportsData?.transports?.find(
      t => t.gmr_transport_id === transportId,
    );
    if (!transport) return nonCallPopoverNormalized;

    const latest = formatTransportStatuses(transport.statuses)[0];
    const latestStatus = latest?.display
      ? { update: latest.display, timestamp: latest.timestamp }
      : null;
    const tripReason = parseTransportVerbatimMessage(
      transport.verbatim_message,
    )?.tripReason;

    if (!latestStatus && !tripReason) return nonCallPopoverNormalized;

    return {
      ...nonCallPopoverNormalized,
      body: {
        ...nonCallPopoverNormalized.body,
        ...(latestStatus ? { status: [latestStatus] } : {}),
        ...(tripReason ? { trip_data: { trip_reason: tripReason } } : {}),
      },
    };
  }, [nonCallPopoverNormalized, isEtlIntegrationEnabled, transportsData]);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, popoverData) => {
      const viewport = createWebMercatorViewport(viewState, basemap);
      if (popoverData) {
        let nonCallLocPosition = [
          parseFloat(popoverData?.body?.location?.longitude),
          parseFloat(popoverData?.body?.location?.latitude),
        ];
        const [x, y] = viewport.project(nonCallLocPosition);

        setPopoverData({
          nonCallData: popoverData,
          x,
          y,
        });
        setPopoverPosition({ x, y });
      }
    },
    [setPopoverPosition, basemap],
  );

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && enrichedPopoverData) {
      const viewState = overlay.getViewState();
      reposition(viewState, enrichedPopoverData);
    }

    if (!enrichedPopoverData) {
      setPopoverPosition(null);
      setPopoverData(null);
    }
  }, [overlay, enrichedPopoverData, reposition]);

  return (
    <>
      {popoverPosition && popoverData && (
        <MemoizedNonCallMapPopover
          x={popoverPosition?.x}
          y={popoverPosition?.y}
          data={popoverData}
        />
      )}
    </>
  );
};

export default NonCallMapPopover;
